import { useNavigate, useSearch } from "@tanstack/react-router";
import { HistoryIcon, SlidersHorizontalIcon } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { MovementListRow } from "@/modules/inventory/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";
import { useMovementsQuery, useMovementsSummaryQuery } from "../api/queries";
import { AdjustmentModal } from "../ui/AdjustmentModal";
import { MovementsFilter } from "../ui/MovementsFilter";
import { MovementsSummaryCards } from "../ui/MovementsSummaryCards";
import { MovementsTable } from "../ui/MovementsTable";

type MovementsSearch = {
  branchId?: string;
  reason?: string;
  dateFrom?: string;
  dateTo?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function MovementsPage() {
  const nav = useNavigate({ from: "/app/inventory/movements" });
  const search = useSearch({
    from: "/app/inventory/movements",
  }) as MovementsSearch;
  const { has } = usePermissions();

  const params = {
    branchId: search.branchId,
    reason: search.reason as MovementListRow["reason"] | undefined,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    sortField: search.sortField ?? "createdAt",
    sortOrder: search.sortOrder ?? "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useMovementsQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useMovementsSummaryQuery({ branchId: search.branchId });
  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const [adjustOpen, setAdjustOpen] = useState(false);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ປະຫວັດ Movement",
              to: "/app/inventory/movements",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <HistoryIcon className="size-5" />
              ປະຫວັດ Movement / Audit
            </h2>
            <p className="text-muted-foreground">
              ກວດສອບການເຄື່ອນໄຫວສະຕ໋ອກ; ປັບຍອດຄົງເຫຼືອດ້ວຍ ADJUST.
            </p>
          </div>
          {has("inventory:update") && (
            <Button onClick={() => setAdjustOpen(true)} className="gap-2">
              <SlidersHorizontalIcon className="size-4" />
              ປັບຍອດ (ADJUST)
            </Button>
          )}
        </div>

        <div className="mb-4">
          <MovementsSummaryCards
            inflowToday={summaryData?.inflowToday ?? 0}
            outflowToday={summaryData?.outflowToday ?? 0}
            netAdjustmentToday={summaryData?.netAdjustmentToday ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <MovementsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <MovementsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
            />
          )}
        </div>

        <AdjustmentModal open={adjustOpen} onOpenChange={setAdjustOpen} />
      </Main>
    </>
  );
}
